/**
 * Il player si apre SOLO dal popup del sito (issue #9).
 *
 * Aperto in una scheda propria — link copiato, email, notifica, preferito — il
 * player salterebbe i passaggi che il sito fa prima di mostrarlo:
 * «Come vuoi seguire il corso?», controllo del profilo ECM, e tutto cio' che
 * vive in `CoursePlayerModal`. Qui, se la finestra non sta dentro un iframe,
 * si rimanda al sito con un link che riapre il popup sullo stesso corso e
 * sulla stessa unita' (`/profilo?tab=corsi&apri=…&unita=…`, letto da
 * `igea-frontend/app/utils/apertura-da-link.ts`).
 *
 * E' un complemento, non la difesa: il controllo lato client si aggira. Le
 * difese vere sono la CloudFront Function della distribuzione `learning`
 * (`igea-infra`) e il middleware dell'LMS (`igea-edx`), con la stessa regola e
 * lo stesso link. Se la forma del link cambia, va cambiata in tutti e quattro.
 *
 * Il controllo gira PRIMA di `initialize()`: niente richieste all'LMS, niente
 * rotte disegnate, `#root` resta vuoto finche' il browser cambia pagina.
 */

/** `course-v1:ORG+NUMERO+EDIZIONE` */
const CHIAVE_CORSO = /^course-v1:[A-Za-z0-9_.-]+\+[A-Za-z0-9_.-]+\+[A-Za-z0-9_.-]+$/;
/** `block-v1:ORG+NUMERO+EDIZIONE+type@vertical+block@<id>`: solo unita', non sequenze. */
const CHIAVE_UNITA = /^block-v1:[A-Za-z0-9_.-]+\+[A-Za-z0-9_.-]+\+[A-Za-z0-9_.-]+\+type@vertical\+block@[A-Za-z0-9_.-]+$/;

/**
 * La finestra e' di primo livello (scheda propria)?
 *
 * Nel dubbio si risponde «no»: senza certezza non si manda via chi potrebbe
 * stare legittimamente nel popup. Il controllo vero sta sul server.
 */
export function inSchedaPropria(finestra = global) {
  try {
    return Boolean(finestra) && finestra.top === finestra;
  } catch (e) {
    return false;
  }
}

function decodifica(segmento) {
  try {
    return decodeURIComponent(segmento);
  } catch (e) {
    return '';
  }
}

/**
 * Corso e unita' dal percorso del player.
 *
 * Forme del player: `/course/<corso>/home`, `/course/<corso>/<sequenza>`,
 * `/course/<corso>/<sequenza>/<unita>`, `/preview/course/<corso>/…`.
 * L'unita' si tiene solo se e' una `vertical` dello stesso corso.
 */
export function corsoEUnitaDalPercorso(percorso) {
  const segmenti = String(percorso || '').split('/').filter(Boolean).map(decodifica);
  const i = segmenti.indexOf('course');
  if (i === -1) { return null; }
  const corso = segmenti[i + 1] || '';
  if (!CHIAVE_CORSO.test(corso)) { return null; }
  const terna = corso.slice('course-v1:'.length);
  const unita = segmenti.slice(i + 2).find(
    (s) => CHIAVE_UNITA.test(s) && s.startsWith(`block-v1:${terna}+`),
  ) || null;
  return { corso, unita };
}

/** Il link del sito che riapre il popup sul corso/unita' (o solo «I miei corsi»). */
export function linkAlSito(sito, percorso) {
  const base = String(sito || '').replace(/\/+$/, '');
  const trovato = corsoEUnitaDalPercorso(percorso);
  const parti = ['tab=corsi'];
  if (trovato) {
    parti.push(`apri=${encodeURIComponent(trovato.corso)}`);
    if (trovato.unita) { parti.push(`unita=${encodeURIComponent(trovato.unita)}`); }
  }
  return `${base}/profilo?${parti.join('&')}`;
}

/**
 * Dov'e' il sito.
 *
 * `IGEA_SITE_URL` dalla configurazione di build; senza, si deduce dal nome del
 * player togliendo `learning.` (`learning.dev.igeacps.click` →
 * `https://dev.igeacps.click`). Se non si deduce nulla: `null`.
 */
export function indirizzoSito(configurato, hostname) {
  if (configurato) { return String(configurato).replace(/\/+$/, ''); }
  const nome = String(hostname || '');
  if (nome.startsWith('learning.') && nome.split('.').length > 2) {
    return `https://${nome.slice('learning.'.length)}`;
  }
  return null;
}

/**
 * L'unico ingresso, chiamato da `src/index.jsx` prima di `initialize()`.
 *
 * Restituisce `true` se il player puo' partire. `false` vuol dire che la
 * pagina sta gia' andando altrove (o che non sa dove andare): non va
 * disegnato nulla.
 *
 * `attivo: false` (`IGEA_SOLO_DAL_POPUP='false'`) spegne il controllo: serve
 * solo allo sviluppo locale, dove il player si apre da solo su localhost.
 */
export function guardiaSoloDalPopup({ finestra = global, sito, attivo = true }) {
  if (!attivo || !inSchedaPropria(finestra)) { return true; }
  const base = indirizzoSito(sito, finestra.location && finestra.location.hostname);
  if (!base) {
    // Senza un sito dove mandare non si mostra comunque nulla: meglio una
    // pagina vuota che un corso aperto fuori dal popup.
    return false;
  }
  finestra.location.replace(linkAlSito(base, finestra.location.pathname));
  return false;
}
